import React from 'react'
import Layout from '../../components/common/Layout'

import PageHeader from '../../components/common/PageHeader'
import ContentSection from '../../components/common/ContentSection'
import FAQ from '../../components/common/FAQ'
import { MapPinAreaIcon } from '@atlas-co/icons'

const faq = [
  {
    title: 'What telecom use cases does Atlas support?',
    content:
      'Atlas supports network planning, tower and cell site management, coverage analysis, fiber route planning, right-of-way assessment, and field crew coordination. Any telecom workflow that depends on spatial data can benefit from Atlas.'
  },
  {
    title: 'How does the AI assistant help telecom teams?',
    content:
      'Navi lets planning and engineering teams ask spatial questions in plain English. "Show me all towers within 10 km of the new highway corridor with capacity below 70%" — and Navi builds the multi-step analysis automatically.'
  },
  {
    title: 'Can Atlas handle large network datasets?',
    content:
      'Yes. Atlas is built to visualize and analyze large-scale spatial datasets. Import tower locations, fiber routes, coverage polygons, and subscriber data — then filter, style, and analyze it all in real time in the browser.'
  },
  {
    title: 'How does Atlas differ from traditional telecom GIS tools?',
    content:
      'Legacy GIS platforms require specialized training, desktop software, and siloed workflows. Atlas is browser-based, AI-powered, and accessible to planning teams, field crews, and executives alike — with Navi handling complex spatial queries so you do not need GIS specialists on every team.'
  },
  {
    title: 'Can I share network maps with stakeholders and regulators?',
    content:
      'Yes. Atlas maps are live, shareable assets. You can send a link to anyone — regulators, local authorities, contractors, or internal leadership — and they can explore the map interactively without needing an Atlas account.'
  }
]

const TelecommunicationsPage = () => {
  return (
    <Layout
      meta={{
        description:
          'Plan networks, manage tower sites, and analyze coverage with AI-powered collaborative GIS. From fiber route planning to field crew coordination.',
        title: 'Atlas for Telecommunications',
        type: 'website'
      }}
      title={'Atlas for Telecommunications'}
    >
      <main>
        <PageHeader
          title={'Telecommunications'}
          description={
            'Plan networks, manage tower sites, and analyze coverage with AI-powered collaborative GIS. From fiber route planning to field crew coordination.'
          }
          video="https://cdn.atlas.co/landing-pages/ev-chargers.mp4"
        />

        <ContentSection centered>
          <h2>Build and manage networks with AI-powered spatial intelligence</h2>
          <p>
            Telecom infrastructure is inherently spatial — every tower, fiber
            segment, and service area is tied to a location. Atlas gives your
            planning, engineering, and field teams a shared map with AI built in
            to accelerate every decision.
          </p>
        </ContentSection>

        <ContentSection
          video={'https://cdn.atlas.co/landing-pages/site-selection.mp4'}
          icon={{ src: '/icons/cell-tower.svg', color: '#CDE4E1' }}
        >
          <h3>Network planning & site selection</h3>
          <p>
            Evaluate candidate sites for new towers, small cells, or fiber
            nodes. Layer in terrain data, population density, existing
            infrastructure, and regulatory boundaries to find optimal
            placements.
          </p>
          <p>
            Atlas lets planning teams compare sites visually, run proximity
            analysis, and share proposals with stakeholders — all from the
            browser.
          </p>
        </ContentSection>

        <ContentSection
          video={'https://cdn.atlas.co/landing-pages/dynamic-dashboards.mp4'}
          reverse
          icon={{ src: <MapPinAreaIcon size={36} />, color: '#C2E5FF' }}
        >
          <h3>Coverage analysis & gap identification</h3>
          <p>
            Visualize signal coverage areas, identify dead zones, and overlay
            subscriber density to prioritize where network investment will have
            the highest impact.
          </p>
          <p>
            Use natural language filtering to quickly search coverage data —
            type "areas with signal strength below -90 dBm and population above
            10,000" and Atlas finds them instantly.
          </p>
        </ContentSection>

        <ContentSection
          video={'https://cdn.atlas.co/landing-pages/network-analysis.mp4'}
          icon={{ src: '/icons/distance.svg', color: '#FFF4E7' }}
        >
          <h3>Fiber & route planning</h3>
          <p>
            Plan fiber optic routes by layering in existing conduit, right-of-way
            boundaries, utility corridors, and permit zones. Measure distances,
            calculate costs per segment, and document every route decision on
            the map.
          </p>
          <p>
            Teams can collaborate on route alternatives in real time, avoiding
            the back-and-forth of static CAD drawings and email attachments.
          </p>
        </ContentSection>

        <ContentSection
          video={'https://cdn.atlas.co/landing-pages/investment-analysis.mp4'}
          reverse
          icon={{ src: '/icons/map-pin-area.svg', color: '#FFBF6E' }}
        >
          <h3>Tower & asset management</h3>
          <p>
            Track every tower, antenna, cabinet, and piece of equipment on a
            single map. Use AI-powered fields to classify equipment types, tag
            maintenance status, or enrich records from descriptions — replacing
            manual data entry with automated intelligence.
          </p>
          <p>
            Mobile access means technicians can pull up site details, log work,
            and capture photos directly in the field.
          </p>
        </ContentSection>

        <ContentSection
          video={'https://cdn.atlas.co/landing-pages/ai-powered-analysis.mp4'}
          icon={{ src: '/icons/lightning.svg', color: '#C2E5FF' }}
        >
          <h3>AI-powered analysis with Navi</h3>
          <p>
            Ask Navi to answer network questions in plain English: "Which towers
            have the highest churn rate within 5 km of a competitor site?" or
            "Show me fiber segments older than 10 years in high-density areas."
            Navi builds the spatial analysis step by step.
          </p>
          <p>
            AI-powered geocoding converts inconsistent site address data into
            precise coordinates, and AI formulas let you compute custom network
            metrics without writing expressions.
          </p>
        </ContentSection>

        <FAQ tabs={faq} />
      </main>
    </Layout>
  )
}

export default TelecommunicationsPage
